import React, { useState, useEffect } from 'react';
import offensiveApi from '../../services/offensiveApi';

const TABS = [
  { id: 'info', label: 'Info' },
  { id: 'summary', label: 'Summary' },
  { id: 'scope', label: 'Scope' },
  { id: 'opinion', label: 'Opinion' },
];

function ReportEditorModal({ engagement, onClose }) {
  const [activeTab, setActiveTab] = useState('info');
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [populating, setPopulating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadConfig();
  }, [engagement.id]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const data = await offensiveApi.getReportConfig(engagement.id);
      setConfig(data);
    } catch (err) {
      setError('Failed to load report config');
    } finally {
      setLoading(false);
    }
  };

  const handleAutoPopulate = async () => {
    try {
      setPopulating(true);
      setError(null);
      const data = await offensiveApi.autoPopulateReportConfig(engagement.id);
      setConfig(data);
    } catch (err) {
      setError('Failed to auto-populate');
    } finally {
      setPopulating(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      await offensiveApi.updateReportConfig(engagement.id, config);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
    setSaved(false);
  };

  // --- List helpers ---
  const addListItem = (field, defaultValue = '') => {
    updateField(field, [...(config[field] || []), defaultValue]);
  };

  const updateListItem = (field, index, value) => {
    const items = [...(config[field] || [])];
    items[index] = value;
    updateField(field, items);
  };

  const removeListItem = (field, index) => {
    updateField(field, (config[field] || []).filter((_, i) => i !== index));
  };

  // --- Scope items helpers ---
  const addScopeItem = () => {
    updateField('scope_items', [...(config.scope_items || []), { type: 'Red', target: '', description: '' }]);
  };

  const updateScopeItem = (index, field, value) => {
    const items = [...(config.scope_items || [])];
    items[index] = { ...items[index], [field]: value };
    updateField('scope_items', items);
  };

  const removeScopeItem = (index) => {
    updateField('scope_items', (config.scope_items || []).filter((_, i) => i !== index));
  };

  const isEmpty = !config || (
    !config.title && !config.target_name && !(config.authors || []).length &&
    !(config.executive_summary || []).length && !(config.opinion_auditor || []).length
  );

  if (loading) {
    return (
      <div className="fixed inset-0 bg-slate-900/70 z-50 flex items-center justify-center">
        <div className="bg-white dark:bg-slate-800 rounded-lg p-8">
          <div className="animate-spin w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full mx-auto" />
          <p className="text-sm text-slate-500 mt-3">Loading report config...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-slate-900/70 z-50 flex items-center justify-center px-4">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Report Editor</h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                {engagement.title} {engagement.engagement_code ? `(${engagement.engagement_code})` : ''}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleAutoPopulate}
                disabled={populating}
                className="px-3 py-1.5 text-xs font-medium rounded-md bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/20 disabled:opacity-50 transition-colors"
                title="Auto-generate report boilerplate from engagement data and findings"
              >
                {populating ? (
                  <span className="flex items-center gap-1.5">
                    <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                    Generating...
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                    Auto-fill
                  </span>
                )}
              </button>
              <button onClick={onClose} className="text-slate-400 hover:text-slate-500">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-3">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  activeTab === tab.id
                    ? 'bg-indigo-500 text-white'
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {error && (
            <div className="bg-rose-100 dark:bg-rose-500/20 text-rose-600 dark:text-rose-400 px-3 py-2 rounded text-sm mb-4">
              {error}
            </div>
          )}

          {isEmpty && !populating && (
            <div className="text-center py-8 mb-4 border border-dashed border-slate-600 rounded-lg">
              <p className="text-slate-400 text-sm mb-3">Report config is empty. Auto-fill from engagement data?</p>
              <button
                onClick={handleAutoPopulate}
                className="px-4 py-2 text-sm font-medium rounded-md bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/25 transition-colors"
              >
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                  Auto-fill Report
                </span>
              </button>
            </div>
          )}

          {activeTab === 'info' && config && (
            <InfoTab
              config={config}
              updateField={updateField}
              authors={config.authors || []}
              addAuthor={() => addListItem('authors')}
              updateAuthor={(i, v) => updateListItem('authors', i, v)}
              removeAuthor={(i) => removeListItem('authors', i)}
            />
          )}
          {activeTab === 'summary' && config && (
            <SummaryTab
              config={config}
              updateField={updateField}
              paragraphs={config.executive_summary || []}
              addParagraph={() => addListItem('executive_summary')}
              updateParagraph={(i, v) => updateListItem('executive_summary', i, v)}
              removeParagraph={(i) => removeListItem('executive_summary', i)}
            />
          )}
          {activeTab === 'scope' && config && (
            <ScopeTab
              config={config}
              addScopeItem={addScopeItem}
              updateScopeItem={updateScopeItem}
              removeScopeItem={removeScopeItem}
              addExclusion={() => addListItem('exclusions')}
              updateExclusion={(i, v) => updateListItem('exclusions', i, v)}
              removeExclusion={(i) => removeListItem('exclusions', i)}
            />
          )}
          {activeTab === 'opinion' && config && (
            <OpinionTab
              config={config}
              updateField={updateField}
              paragraphs={config.opinion_auditor || []}
              addParagraph={() => addListItem('opinion_auditor')}
              updateParagraph={(i, v) => updateListItem('opinion_auditor', i, v)}
              removeParagraph={(i) => removeListItem('opinion_auditor', i)}
            />
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-slate-200 dark:border-slate-700 flex justify-between items-center flex-shrink-0">
          <div className="text-sm">
            {saved && <span className="text-emerald-500 font-medium">Saved</span>}
          </div>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 dark:text-slate-300 border border-slate-300 dark:border-slate-600 rounded hover:bg-slate-50 dark:hover:bg-slate-700">
              Close
            </button>
            <button onClick={handleSave} disabled={saving} className="px-4 py-2 text-sm bg-indigo-500 hover:bg-indigo-600 text-white rounded disabled:opacity-50">
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ============================================================================
// Tab Components
// ============================================================================

function InfoTab({ config, updateField, authors, addAuthor, updateAuthor, removeAuthor }) {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Report Title</label>
        <input
          type="text"
          value={config.title || ''}
          onChange={e => updateField('title', e.target.value)}
          placeholder="Revisión Independiente de Ciberseguridad"
          className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        />
        <p className="text-xs text-slate-400 mt-1">Main title on the report cover page</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Subtitle</label>
        <input
          type="text"
          value={config.subtitle || ''}
          onChange={e => updateField('subtitle', e.target.value)}
          placeholder="e.g. Evaluación de Seguridad Inalámbrica"
          className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        />
        <p className="text-xs text-slate-400 mt-1">
          Sits under the main title on the cover. Left blank, the report uses the assessment type.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Target Name</label>
        <input
          type="text"
          value={config.target_name || ''}
          onChange={e => updateField('target_name', e.target.value)}
          placeholder="e.g. Infraestructura Mobile ID (mID)"
          className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        />
        <p className="text-xs text-slate-400 mt-1">Appears on the report cover page below the customer name</p>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Version</label>
          <input
            type="text"
            value={config.version || ''}
            onChange={e => updateField('version', e.target.value)}
            placeholder="1.0"
            className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Report Date</label>
          <input
            type="text"
            value={config.report_date || ''}
            onChange={e => updateField('report_date', e.target.value)}
            placeholder="2026-07-02"
            className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Contacts</label>
          <input
            type="text"
            value={config.contacts || ''}
            onChange={e => updateField('contacts', e.target.value)}
            placeholder="el equipo técnico y de seguridad"
            className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
      </div>

      {/* Authors */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Authors</label>
          <button onClick={addAuthor} className="text-xs text-indigo-500 hover:text-indigo-600 font-medium">+ Add Author</button>
        </div>
        {authors.length === 0 && (
          <p className="text-xs text-slate-400 italic">No authors added. Click "+ Add Author" to add one.</p>
        )}
        <div className="space-y-2">
          {authors.map((author, i) => (
            <div key={i} className="flex gap-2">
              <input
                type="text"
                value={author}
                onChange={e => updateAuthor(i, e.target.value)}
                placeholder="Name, Certification"
                className="flex-1 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
              <button onClick={() => removeAuthor(i)} className="text-rose-400 hover:text-rose-500 px-2" title="Remove">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Acknowledgment */}
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Acknowledgment</label>
        <textarea
          value={config.acknowledgment || ''}
          onChange={e => updateField('acknowledgment', e.target.value)}
          placeholder="Queremos expresar nuestro agradecimiento a..."
          rows={3}
          className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-y"
        />
        <p className="text-xs text-slate-400 mt-1">Reconocimiento section in the report</p>
      </div>
    </div>
  );
}


function SummaryTab({ config, updateField, paragraphs, addParagraph, updateParagraph, removeParagraph }) {
  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Executive Summary</label>
          <button onClick={addParagraph} className="text-xs text-indigo-500 hover:text-indigo-600 font-medium">+ Add Paragraph</button>
        </div>
        <p className="text-xs text-slate-400 mb-3">Each entry becomes a paragraph (or numbered list) in the Resumen Ejecutivo section.</p>
      </div>
      {paragraphs.length === 0 && (
        <p className="text-xs text-slate-400 italic">No summary paragraphs. Use Auto-fill to generate from findings, or add manually.</p>
      )}
      <div className="space-y-3">
        {paragraphs.map((para, i) => (
          <div key={i} className="relative">
            <div className="flex items-start gap-2">
              <span className="text-xs text-slate-400 mt-2.5 w-4 text-right flex-shrink-0">{i + 1}</span>
              <textarea
                value={para}
                onChange={e => updateParagraph(i, e.target.value)}
                placeholder="Write a paragraph of the executive summary..."
                rows={4}
                className="flex-1 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-y"
              />
              <button onClick={() => removeParagraph(i)} className="text-rose-400 hover:text-rose-500 mt-2 flex-shrink-0" title="Remove paragraph">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function ScopeTab({ config, addScopeItem, updateScopeItem, removeScopeItem, addExclusion, updateExclusion, removeExclusion }) {
  const scopeItems = config.scope_items || [];
  const exclusions = config.exclusions || [];

  return (
    <div className="space-y-6">
      {/* Scope Items */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Scope Items</label>
          <button onClick={addScopeItem} className="text-xs text-indigo-500 hover:text-indigo-600 font-medium">+ Add Item</button>
        </div>
        <p className="text-xs text-slate-400 mb-3">Define targets with type, identifier, and description for the report scope section</p>
        {scopeItems.length === 0 && (
          <p className="text-xs text-slate-400 italic">No scope items. If empty, scope will be auto-generated from the engagement's configured scope.</p>
        )}
        <div className="space-y-3">
          {scopeItems.map((item, i) => (
            <div key={i} className="border border-slate-200 dark:border-slate-600 rounded-lg p-3 bg-slate-50 dark:bg-slate-700/50">
              <div className="flex gap-2 mb-2">
                <select
                  value={item.type || 'Red'}
                  onChange={e => updateScopeItem(i, 'type', e.target.value)}
                  className="w-32 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 px-2 py-1.5 text-sm"
                >
                  <option value="Red">Red</option>
                  <option value="Dominio">Dominio</option>
                  <option value="Aplicación">Aplicación</option>
                  <option value="Servicio">Servicio</option>
                  <option value="API">API</option>
                  <option value="Host">Host</option>
                </select>
                <input
                  type="text"
                  value={item.target || ''}
                  onChange={e => updateScopeItem(i, 'target', e.target.value)}
                  placeholder="172.17.80.0/24 or domain.com"
                  className="flex-1 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 px-3 py-1.5 text-sm font-mono"
                />
                <button onClick={() => removeScopeItem(i)} className="text-rose-400 hover:text-rose-500 px-2" title="Remove">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <input
                type="text"
                value={item.description || ''}
                onChange={e => updateScopeItem(i, 'description', e.target.value)}
                placeholder="Description of this target"
                className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 px-3 py-1.5 text-sm"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Exclusions */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Exclusions</label>
          <button onClick={addExclusion} className="text-xs text-indigo-500 hover:text-indigo-600 font-medium">+ Add Exclusion</button>
        </div>
        <p className="text-xs text-slate-400 mb-3">Activities or targets explicitly excluded from testing</p>
        {exclusions.length === 0 && (
          <p className="text-xs text-slate-400 italic">No exclusions defined.</p>
        )}
        <div className="space-y-2">
          {exclusions.map((exc, i) => (
            <div key={i} className="flex gap-2">
              <input
                type="text"
                value={exc}
                onChange={e => updateExclusion(i, e.target.value)}
                placeholder="Describe what was excluded and why"
                className="flex-1 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 px-3 py-2 text-sm"
              />
              <button onClick={() => removeExclusion(i)} className="text-rose-400 hover:text-rose-500 px-2" title="Remove">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


function OpinionTab({ config, updateField, paragraphs, addParagraph, updateParagraph, removeParagraph }) {
  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Auditor's Opinion</label>
          <button onClick={addParagraph} className="text-xs text-indigo-500 hover:text-indigo-600 font-medium">+ Add Paragraph</button>
        </div>
        <p className="text-xs text-slate-400 mb-3">Executive opinion from the auditor. Each entry becomes a paragraph in the report.</p>
      </div>

      {/* Only the failure state is surfaced. 'llm' and 'manual' are both fine
          and need no badge; 'no_findings' is the correct output when there is
          nothing to analyse. 'template' means generation was attempted and
          failed, so generic boilerplate is sitting in a deliverable. */}
      {/* Rendering reproduces the stored opinion rather than regenerating it, so
          an opinion written before the findings existed goes out verbatim. */}
      {config?.opinion_auditor_stale && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3">
          <div className="flex items-start gap-2">
            <svg className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="text-xs">
              <p className="font-semibold text-amber-500">The findings changed after this opinion was written</p>
              <p className="text-slate-400 mt-1">
                It will be used in the report exactly as it appears here. Run Auto-fill to rewrite it
                against the current findings.
              </p>
            </div>
          </div>
        </div>
      )}

      {config?.opinion_auditor_source === 'template' && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3">
          <div className="flex items-start gap-2">
            <svg className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            <div className="text-xs">
              <p className="font-semibold text-amber-500">This opinion is generic boilerplate</p>
              <p className="text-slate-400 mt-1">
                Generation failed, so placeholder text was used instead of an analysis of the findings.
                Re-run Auto-fill, or rewrite it here, before delivering the report.
              </p>
            </div>
          </div>
        </div>
      )}
      {paragraphs.length === 0 && (
        <p className="text-xs text-slate-400 italic">No opinion paragraphs. Use Auto-fill to generate from findings.</p>
      )}
      <div className="space-y-3">
        {paragraphs.map((para, i) => (
          <div key={i} className="relative">
            <div className="flex items-start gap-2">
              <span className="text-xs text-slate-400 mt-2.5 w-4 text-right flex-shrink-0">{i + 1}</span>
              <textarea
                value={para}
                onChange={e => updateParagraph(i, e.target.value)}
                placeholder="Write a paragraph of the auditor's opinion..."
                rows={4}
                className="flex-1 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-y"
              />
              <button onClick={() => removeParagraph(i)} className="text-rose-400 hover:text-rose-500 mt-2 flex-shrink-0" title="Remove paragraph">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


export default ReportEditorModal;
